// Apolo IDE — useGitChecks (Pedro 2026-06-25, sprint cursor-strip-3).
//
// Hook que busca check-runs reais do GitHub Actions pra (owner, repo, ref)
// via Worker (/api/github/checks). Sem mockup.
//
// API: { data, loading, error, refresh }
//   data = { rollup, summary: {passed, failed, pending, skipped, neutral, total}, items[] }
// Não inicia fetch sem owner+repo+ref. Polling opcional (default 30s).

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';

const DEFAULT_POLL_MS = 30_000;

export function useGitChecks({ owner, repo, ref, enabled = true, pollMs = DEFAULT_POLL_MS } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    if (!owner || !repo || !ref) return;
    abortRef.current?.abort?.();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ owner, repo, ref }).toString();
      // apiFetch resolves a parsed envelope { ok, status, data, error? } —
      // NOT a Response. Não chamar res.json() aqui.
      const res = await apiFetch(`/api/github/checks?${qs}`, { signal: ac.signal });
      if (!res.ok) {
        if (res.status === 404 && (res.data?.code === 'GITHUB_NOT_CONNECTED' || res.error === 'GitHub not connected')) {
          setError('GitHub não conectado');
        } else {
          setError(res.error || `Falha HTTP ${res.status}`);
        }
        setData(null);
        return;
      }
      setData(res.data);
      setError(null);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setError(e?.message || 'Falha ao buscar checks');
      setData(null);
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }, [owner, repo, ref]);

  // Initial fetch + polling. Abort on unmount / arg change.
  useEffect(() => {
    if (!enabled || !owner || !repo || !ref) return;
    fetchOnce();
    if (pollMs > 0) {
      const id = setInterval(fetchOnce, pollMs);
      return () => {
        clearInterval(id);
        abortRef.current?.abort?.();
      };
    }
    return () => abortRef.current?.abort?.();
  }, [enabled, owner, repo, ref, pollMs, fetchOnce]);

  return { data, loading, error, refresh: fetchOnce };
}

export default useGitChecks;
